import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Grid, Column, Tile, TextInput, TextArea, InlineNotification } from '@carbon/react';
import { ArrowLeft, Add, Home, Building, DeliveryTruck, Temperature, Package, Location, CheckmarkFilled } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useZones } from '../hooks/useZones';
import { useSensorsOptimized } from '../hooks/useSensorsOptimized';
import { useSubscription } from '../hooks/useSubscription';
import { updateZone } from '../services/zoneService';
import AddressAutosuggestInput from '../components/AddressAutosuggestInput';
import SubscriptionGate from '../components/SubscriptionGate';

const PRESET_COLORS = [
  '#FF3B30', // Red
  '#FF9500', // Orange
  '#FFCC00', // Yellow
  '#34C759', // Green
  '#007AFF', // Blue
  '#5856D6', // Purple
  '#FF2D92', // Pink
  '#8E8E93', // Gray
  '#00C7BE', // Teal
  '#6D1B7B', // Dark Purple
  '#FF6B35', // Coral
  '#1B7B32', // Dark Green
];

const ZONE_TYPES = [
  { value: 'room', label: 'Room', icon: Home },
  { value: 'office', label: 'Office', icon: Building },
  { value: 'truck', label: 'Truck', icon: DeliveryTruck },
  { value: 'cold_room', label: 'Cold Room', icon: Temperature },
  { value: 'refrigerator', label: 'Refrigerator', icon: Package },
  { value: 'esky', label: 'Esky/Cooler', icon: Package },
  { value: 'other', label: 'Other', icon: Location },
];

const AddZonePage = () => {
  const navigate = useNavigate();
  const { id: zoneId } = useParams(); // Get zone ID from URL for edit mode
  const { theme } = useTheme();
  const { canAddZones } = useSubscription();
  const { zones, addZone } = useZones();
  const { sensors } = useSensorsOptimized();

  const isEditMode = !!zoneId; // Determine if we're editing or creating

  const [zoneName, setZoneName] = useState('');
  const [selectedType, setSelectedType] = useState('room');
  const [customType, setCustomType] = useState('');
  const [description, setDescription] = useState('');
  const [selectedColor, setSelectedColor] = useState(PRESET_COLORS[0]);
  const [selectedSensors, setSelectedSensors] = useState([]);
  // Address fields
  const [streetAddress, setStreetAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [country, setCountry] = useState('US');
  const [coordinates, setCoordinates] = useState({ latitude: null, longitude: null });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  // Load existing zone data when in edit mode
  useEffect(() => {
    if (isEditMode && zones.length > 0) {
      const zone = zones.find(z => z.id === zoneId);
      if (zone) {
        setZoneName(zone.name || '');
        setSelectedType(zone.type || zone.zone_type || 'room');
        setCustomType(zone.custom_type || '');
        setDescription(zone.description || '');
        setSelectedColor(zone.color || PRESET_COLORS[0]);
        setSelectedSensors(zone.sensor_ids || []);
        setStreetAddress(zone.street_address || '');
        setCity(zone.city || '');
        setState(zone.state || '');
        setZipCode(zone.zip_code || '');
        setCountry(zone.country || 'US');
        setCoordinates({
          latitude: zone.latitude || null,
          longitude: zone.longitude || null,
        });
      }
    }
  }, [isEditMode, zoneId, zones]);

  // Get available sensors (unallocated to any zone or allocated to current zone if editing)
  const getAvailableSensors = () => {
    const allocatedSensorIds = new Set();
    zones.forEach(zone => {
      // Skip current zone when editing
      if (isEditMode && zone.id === zoneId) return;
      
      if (zone.sensor_ids && Array.isArray(zone.sensor_ids)) {
        zone.sensor_ids.forEach(sensorId => {
          allocatedSensorIds.add(sensorId);
        });
      }
    });
    
    return sensors.filter(sensor => !allocatedSensorIds.has(sensor.id));
  };

  const availableSensors = getAvailableSensors();

  const handleAddressSelected = (address) => {
    // Auto-fill address fields when user selects from suggestions
    console.log('🗺️ Address selected:', address);
    console.log('🗺️ Address fields:', {
      streetAddress: address.streetAddress,
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
      country: address.country,
      latitude: address.latitude,
      longitude: address.longitude
    });
    
    setStreetAddress(address.streetAddress || '');
    setCity(address.city || '');
    setState(address.state || '');
    setZipCode(address.zipCode || '');
    setCountry(address.country || 'US');
    setCoordinates({
      latitude: address.latitude,
      longitude: address.longitude,
    });
  };

  const toggleSensorSelection = (sensorId) => {
    setSelectedSensors(prev => 
      prev.includes(sensorId) 
        ? prev.filter(id => id !== sensorId)
        : [...prev, sensorId]
    );
  };

  const handleSaveZone = async () => {
    if (!zoneName.trim()) {
      setError('Please enter a zone name');
      return;
    }

    if (selectedType === 'other' && !customType.trim()) {
      setError('Please enter a custom zone type');
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      const addressFormatted = [
        streetAddress.trim(),
        city.trim(),
        state.trim(),
        zipCode.trim()
      ].filter(Boolean).join(', ') || undefined;

      const zoneData = {
        name: zoneName.trim(),
        type: selectedType,
        custom_type: selectedType === 'other' ? customType.trim() : undefined,
        description: description.trim() || undefined,
        color: selectedColor,
        sensor_ids: selectedSensors,
        gateway_ids: [],
        // Address fields
        street_address: streetAddress.trim() || undefined,
        city: city.trim() || undefined,
        state: state.trim() || undefined,
        zip_code: zipCode.trim() || undefined,
        country: country || 'US',
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        address_formatted: addressFormatted,
      };

      if (isEditMode) {
        // Update existing zone
        await updateZone(zoneId, zoneData);
        // Zones will auto-update via real-time subscription
      } else {
        // Create new zone
        await addZone(zoneData);
      }
      navigate('/dashboard/zones');
    } catch (err) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} zone:`, err);
      setError(`Failed to ${isEditMode ? 'update' : 'create'} zone: ${err.message || 'Please try again'}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SubscriptionGate 
      feature="adding zones" 
      disabled={!canAddZones}
      customMessage="You need an active subscription to add zones to your cold chain monitoring system."
    >
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '48px 0' }} data-testid="add-zone-page">
      <Grid>
        <Column lg={16} md={8} sm={4}>
          <Button
            kind="ghost"
            renderIcon={ArrowLeft}
            onClick={() => navigate('/dashboard/zones')}
            style={{ marginBottom: '24px' }}
          >
            Back to Zones
          </Button>
          <h1 style={{ color: textPrimary, fontSize: '2rem', fontWeight: 600, marginBottom: '32px' }}>
            {isEditMode ? 'Edit Zone' : 'Create New Zone'}
          </h1>
        </Column>

        <Column lg={10} md={5} sm={4}>
          {error && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={error}
              onClose={() => setError('')}
              style={{ marginBottom: '24px' }}
            />
          )}

          {/* Zone Name Section */}
          <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '32px', marginBottom: '24px' }}>
            <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, marginBottom: '16px' }}>Zone Name</h2>
            <TextInput
              id="zoneName"
              labelText=""
              placeholder="Enter zone name (e.g., Living Room, Kitchen)"
              value={zoneName}
              onChange={(e) => setZoneName(e.target.value)}
              maxLength={50}
              data-testid="zone-name"
            />
          </Tile>

          {/* Zone Type Section */}
          <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '32px', marginBottom: '24px' }}>
            <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, marginBottom: '16px' }}>Zone Type</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px' }}>
              {ZONE_TYPES.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.value}
                    onClick={() => setSelectedType(type.value)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '16px 12px',
                      backgroundColor: selectedType === type.value ? bgPrimary : bgSecondary,
                      border: `1px solid ${selectedType === type.value ? '#0f62fe' : borderColor}`,
                      borderRadius: '4px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    <Icon size={24} style={{ color: selectedType === type.value ? '#0f62fe' : textSecondary, marginBottom: '8px' }} />
                    <span style={{
                      fontSize: '12px',
                      color: selectedType === type.value ? textPrimary : textSecondary,
                      fontWeight: selectedType === type.value ? 600 : 500
                    }}>
                      {type.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </Tile>

          {/* Custom Type Input (when "Other" is selected) */}
          {selectedType === 'other' && (
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '32px', marginBottom: '24px' }}>
              <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, marginBottom: '16px' }}>Custom Zone Type</h2>
              <TextInput
                id="customType"
                labelText=""
                placeholder="Enter custom zone type (e.g., Warehouse, Lab)"
                value={customType}
                onChange={(e) => setCustomType(e.target.value)}
                maxLength={30}
              />
            </Tile>
          )}

          {/* Description Section */}
          <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '32px', marginBottom: '24px' }}>
            <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, marginBottom: '16px' }}>Description (Optional)</h2>
              <TextArea
                id="description"
              labelText=""
              placeholder="Add a description for this zone..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
                rows={3}
              maxLength={200}
            />
          </Tile>

          {/* Address Section */}
          <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '32px', marginBottom: '24px' }}>
            <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, marginBottom: '16px' }}>Address (Optional)</h2>
            <div style={{ marginBottom: '16px' }}>
              <AddressAutosuggestInput
                placeholder="Street Address (e.g., 123 Main St, City, State)"
                value={streetAddress}
                onChangeText={setStreetAddress}
                onAddressSelected={handleAddressSelected}
                maxLength={100}
                searchOnlyOnUserInput={true}
              />
            </div>
              <div style={{ display: 'flex', gap: '12px' }}>
              <TextInput
                id="city"
                labelText=""
                placeholder="City"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                maxLength={50}
                style={{ flex: 1 }}
              />
              <TextInput
                id="state"
                labelText=""
                placeholder="State"
                value={state}
                onChange={(e) => setState(e.target.value)}
                maxLength={20}
                style={{ flex: 1 }}
              />
              <TextInput
                id="zipCode"
                labelText=""
                placeholder="ZIP"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                maxLength={10}
                style={{ flex: '0.6' }}
              />
            </div>
          </Tile>

          {/* Sensor Selection Section */}
          <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '32px', marginBottom: '24px' }}>
            <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, marginBottom: '16px' }}>Select Sensors (Optional)</h2>
            {availableSensors.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '20px',
                backgroundColor: bgPrimary,
                borderRadius: '4px',
                border: `1px solid ${borderColor}`
              }}>
                <p style={{ fontSize: '16px', color: textPrimary, fontWeight: 600, marginBottom: '4px' }}>
                  All sensors are already allocated to zones
                </p>
                <p style={{ fontSize: '14px', color: textSecondary }}>
                  Edit existing zones to reallocate sensors or add new sensors
                </p>
              </div>
            ) : (
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {availableSensors.map((sensor) => (
                  <div
                    key={sensor.id}
                    onClick={() => toggleSensorSelection(sensor.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      backgroundColor: selectedSensors.includes(sensor.id) ? '#0f62fe20' : bgPrimary,
                      border: `1px solid ${selectedSensors.includes(sensor.id) ? '#0f62fe' : borderColor}`,
                      borderRadius: '4px',
                      cursor: 'pointer',
                      marginBottom: '8px',
                      transition: 'all 0.2s',
                    }}
                  >
                    <div>
                      <p style={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: textPrimary,
                        marginBottom: '2px'
                      }}>
                        {sensor.name}
                      </p>
                      <p style={{ fontSize: '12px', color: textSecondary }}>
                        ID: {sensor.device_id || sensor.id}
                      </p>
                    </div>
                    {selectedSensors.includes(sensor.id) && (
                      <CheckmarkFilled size={20} style={{ color: '#0f62fe' }} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </Tile>

          {/* Color Selection Section */}
          <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '32px', marginBottom: '24px' }}>
            <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, marginBottom: '16px' }}>Choose Color</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(44px, 1fr))', gap: '12px' }}>
              {PRESET_COLORS.map((color, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedColor(color)}
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    backgroundColor: color,
                    border: selectedColor === color ? '2px solid #0f62fe' : '2px solid transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                  }}
                >
                  {selectedColor === color && (
                    <CheckmarkFilled size={16} style={{ color: '#FFFFFF' }} />
                  )}
                </button>
              ))}
            </div>
          </Tile>

          {/* Preview Section */}
          <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '32px', marginBottom: '24px' }}>
            <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, marginBottom: '16px' }}>Preview</h2>
            <div style={{
              backgroundColor: bgPrimary,
              borderRadius: '4px',
              padding: '16px',
              borderLeft: `4px solid ${selectedColor}`
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '12px'
              }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: selectedColor,
                  marginRight: '12px'
                }} />
                <span style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: textPrimary
                }}>
                  {zoneName.trim() || 'Zone Name'}
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between'
              }}>
                <span style={{ fontSize: '14px', color: textSecondary }}>
                  {selectedSensors.length} sensors
                </span>
                <span style={{ fontSize: '14px', color: textSecondary }}>
                  0 gateways
                </span>
                </div>
                </div>
          </Tile>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Button kind="secondary" onClick={() => navigate('/dashboard/zones')}>Cancel</Button>
            <Button 
              kind="primary" 
              renderIcon={Add} 
              onClick={handleSaveZone} 
              disabled={!zoneName.trim() || isLoading || (selectedType === 'other' && !customType.trim())}
              data-testid="save-zone"
            >
              {isLoading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Zone' : 'Create Zone')}
            </Button>
          </div>
        </Column>
      </Grid>
    </div>
    </SubscriptionGate>
  );
};

export default AddZonePage;
